import { Link } from "react-router-dom";
import { ArrowRight, Gauge, Thermometer, Wind, Factory, Lightbulb, Layers, FileText, UserRound, MapPin, CircleCheck } from "lucide-react";
import { Section, Steps, FeatureGrid, FinalCTA } from "@/components/v2/blocks";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { CardContent } from "@/components/ui/card";
import TraceCard from "@/components/v2/TraceCard";
import Seo from "@/components/v2/Seo";
import PageHeroPhoto from "@/components/v2/PageHeroPhoto";
import { SECTEURS_CEE } from "@/pages/v2/SecteurPrimesCee";

/**
 * /professionnels/primes-cee — pendant « professionnels » de la page Primes CEE.
 *
 * La page /primes-cee s'adresse au grand public (coups de pouce, rénovation
 * d'ampleur). Celle-ci parle le langage des entreprises, collectivités et
 * copropriétés : gisements par usage, secteurs d'activité, multi-sites,
 * valorisation au meilleur prix du kWh cumac.
 */

const TRACE_COLORS = ["#2E7D62", "#7AB317", "#0E9C8A"];

const OPERATIONS = [
  {
    icon: Gauge,
    title: "GTB & pilotage énergétique",
    text: "L'opération phare du tertiaire : une gestion technique du bâtiment de classe A ou B pilote chauffage, ventilation, climatisation et éclairage, et objective vos gains.",
  },
  {
    icon: Thermometer,
    title: "Calorifugeage des réseaux",
    text: "Isolation des réseaux hydrauliques et des points singuliers (vannes, échangeurs) : un gisement rapide, peu invasif et fortement valorisé.",
  },
  {
    icon: Wind,
    title: "Air comprimé & moteurs",
    text: "Variation électronique de vitesse, séquenceurs, récupération de chaleur sur compresseurs : des retours sur investissement souvent inférieurs à deux ans.",
  },
  {
    icon: Factory,
    title: "Chaleur fatale",
    text: "Récupération de la chaleur des fumées, des groupes froid et des process pour la réinjecter dans vos utilités, votre eau chaude ou un réseau.",
  },
  {
    icon: Lightbulb,
    title: "Éclairage performant",
    text: "Relamping LED avec détection et gradation dans les bureaux, entrepôts, parkings et surfaces de vente — un poste immédiatement mesurable.",
  },
  {
    icon: Layers,
    title: "Enveloppe du bâtiment",
    text: "Toitures-terrasses, combles, planchers et déstratification des grands volumes : réduire les besoins avant de produire mieux.",
  },
];

const ETAPES = [
  { title: "Vous nous décrivez votre projet", text: "Bâtiments, usages, travaux envisagés. Un devis d'entreprise ou un audit suffit pour démarrer." },
  { title: "Nous qualifions les fiches CEE", text: "Nous identifions les opérations standardisées mobilisables et les bonifications applicables à votre situation." },
  { title: "Nous constituons le dossier", text: "Pièces, attestations et engagements sont réunis dans les règles, avant le démarrage des travaux." },
  { title: "Nous sécurisons le dossier", text: "Antériorité, pièces, attestations et contrôle post-chantier : le dossier est tenu jusqu'au versement de la prime." },
];

const FAQ_PRO = [
  ["Ma structure est-elle éligible ?", "Le dispositif est ouvert à toutes les structures : entreprises de toutes tailles, commerces, industries, exploitations agricoles, établissements de santé, collectivités, syndics et copropriétés. Il n'y a pas de condition de taille ni de chiffre d'affaires."],
  ["La prime est-elle cumulable avec d'autres aides ?", "Oui, dans la limite prévue par chaque dispositif. Les CEE se cumulent notamment avec les aides régionales et les dispositifs sectoriels. Nous vérifions les cumuls avant de vous engager."],
  ["Quand faut-il nous solliciter ?", "Avant la signature du devis de travaux. L'antériorité du dossier CEE conditionne l'éligibilité : un devis signé avant l'ouverture du dossier ne peut plus être valorisé."],
  ["Comment est calculé le montant ?", "Chaque opération correspond à une fiche standardisée qui donne un volume de kWh cumac, selon la zone climatique, la surface ou la puissance. Ce volume est ensuite valorisé à un prix négocié auprès des obligés."],
  ["Gérez-vous les dossiers multi-sites ?", "Oui. Les portefeuilles de sites sont traités globalement : un même dossier peut regrouper plusieurs établissements, avec un suivi consolidé des travaux et des primes."],
];

export default function PrimesCeePro() {
  return (
    <div>
      <Seo
        title="Primes CEE pour les professionnels"
        description="Financez vos travaux d'efficacité énergétique grâce aux Certificats d'Économies d'Énergie. Energy Market monte et suit votre dossier, sans reste à charge, pour les entreprises, collectivités et copropriétés."
        path="/professionnels/primes-cee"
      />

      <PageHeroPhoto
        cmsKey="cee-pro"
        eyebrow="Primes CEE · Entreprises & collectivités"
        title="Financez vos projets d'efficacité énergétique"
        subtitle="Nous vérifions votre éligibilité, identifions les opérations finançables et montons le dossier jusqu'au versement de la prime."
        image="/img/cee-pro/hero.webp"
        overlay="brand-light"
        parallax
        ctaLabel="Étudier mon projet"
        ctaTo="/contact?type=cee-pro"
        highlightsLabel="Votre projet CEE"
        highlightsNumbered
        highlights={[
          "Étude de votre éligibilité",
          "Identification des opérations finançables",
          "Montage et suivi du dossier",
        ]}
        bottomCards={[
          { label: "Bâtiments tertiaires", image: "/img/cee-pro/tertiaire.webp", to: "/professionnels/primes-cee/secteur/bureaux" },
          { label: "Industrie", image: "/img/cee-pro/industrie.webp", to: "/professionnels/primes-cee/secteur/industrie" },
          { label: "Transport & logistique", image: "/img/cee-pro/transport.webp", to: "/professionnels/primes-cee/secteur/commerce" },
          { label: "Collectivités", image: "/img/cee-pro/collectivites.webp", to: "/professionnels/primes-cee/secteur/collectivites" },
        ]}
        reassurance={[
          { label: "Étude gratuite", icon: FileText },
          { label: "Interlocuteur dédié", icon: UserRound },
          { label: "Accompagnement national", icon: MapPin },
          { label: "Dossier suivi de A à Z", icon: CircleCheck },
        ]}
      />

      <Section
        cmsKey="cee-pro.bref"
        eyebrow="Comprendre"
        title="Les CEE, côté entreprise"
        intro="Le dispositif des Certificats d'Économies d'Énergie impose aux vendeurs d'énergie — les « obligés » — de financer des économies d'énergie chez leurs clients. Pour une entreprise, une collectivité ou une copropriété, c'est un financement direct de travaux déjà nécessaires, sans contrepartie commerciale."
      >
        <FeatureGrid
          items={[
            { title: "Une obligation d'État", text: "Créé par la loi POPE, piloté par le ministère de la Transition écologique. La 6ᵉ période 2026-2030 fixe une obligation record : les financements disponibles augmentent fortement." },
            { title: "Toutes les structures", text: "Entreprises de toutes tailles, commerces, industries, exploitations agricoles, établissements de santé, collectivités, syndics et copropriétés." },
            { title: "Un prix qui se négocie", text: "Le kWh cumac n'a pas de prix fixe. À opérations identiques, l'écart entre deux offres de valorisation se chiffre en milliers d'euros." },
          ]}
        />
      </Section>

      <Section
        cmsKey="cee-pro.operations"
        eyebrow="Gisements"
        title="Les opérations les plus valorisées"
        intro="Sur un bâtiment tertiaire ou un site industriel, quelques familles d'opérations concentrent l'essentiel des primes. Elles se combinent souvent dans un même dossier."
        muted
      >
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {OPERATIONS.map(({ icon: Icon, title, text }) => (
            <div key={title} className="rounded-2xl border border-border bg-background p-6 shadow-sm">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-accent text-accent-foreground">
                <Icon className="h-5 w-5" strokeWidth={2.2} aria-hidden />
              </span>
              <h3 className="font-display mt-4 text-lg font-bold text-primary">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{text}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section
        cmsKey="cee-pro.secteurs"
        eyebrow="Secteurs"
        title="Votre secteur d'activité"
        intro="Chaque secteur a ses gisements d'économies d'énergie et ses fiches CEE. Choisissez le vôtre : nous identifions les opérations finançables sans reste à charge."
      >
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-5">
          {SECTEURS_CEE.map((sec) => (
            <Link
              key={sec.slug}
              to={`/professionnels/primes-cee/secteur/${sec.slug}`}
              className="group relative flex h-52 flex-col justify-end overflow-hidden rounded-2xl border border-border bg-secondary shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-accent/60 hover:shadow-xl sm:h-64"
            >
              <img
                src={`/img/secteurs-cee/${sec.slug}.webp`}
                alt=""
                aria-hidden
                loading="lazy"
                decoding="async"
                className="absolute inset-0 h-full w-full scale-105 object-cover blur-[3px] transition-all duration-500 group-hover:scale-110 group-hover:blur-0"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#051E14]/95 via-[#051E14]/40 to-transparent" aria-hidden />
              <span className="absolute left-3.5 top-3.5 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/20 shadow-md ring-1 ring-white/40 backdrop-blur-md transition-transform duration-300 group-hover:scale-110">
                <img src={sec.picto} alt="" aria-hidden loading="lazy" decoding="async" className="h-8 w-8 drop-shadow" />
              </span>
              <div className="relative z-10 p-4 sm:p-[18px]">
                <p className="font-display text-[16px] font-bold leading-snug text-white [text-shadow:0_1px_8px_rgba(0,0,0,0.35)] sm:text-[17px]">
                  {sec.nom}
                </p>
                <span className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold text-accent">
                  Découvrir{" "}
                  <ArrowRight className="h-3 w-3 transition-transform duration-300 group-hover:translate-x-0.5" aria-hidden />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      <Section
        cmsKey="cee-pro.parcours"
        eyebrow="Comment ça se passe"
        title="Un dossier tenu de bout en bout"
        intro="Le point de vigilance est toujours le même : nous solliciter avant la signature du devis de travaux. L'antériorité du dossier conditionne l'éligibilité."
        muted
      >
        <Steps steps={ETAPES} />
      </Section>

      <Section cmsKey="cee-pro.faq" eyebrow="Questions fréquentes" title="Ce que les entreprises nous demandent">
        <div className="mx-auto max-w-3xl">
          <Accordion type="single" collapsible className="w-full">
            {FAQ_PRO.map(([q, a], i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger>{q}</AccordionTrigger>
                <AccordionContent>{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Section>

      {/* ── Primes CEE pro — bandeau photo + badge ×5 ── */}
      <section
        className="relative overflow-hidden bg-primary text-primary-foreground"
        style={{ backgroundImage: "radial-gradient(circle at 28% 50%, rgba(8,90,66,0.43), transparent 40%)" }}
      >
        {/* Photo pleine hauteur, collée aux bords haut/droit/bas (desktop) */}
        <div className="absolute inset-y-0 right-0 hidden w-1/2 lg:block">
          <div className="relative h-full w-full overflow-hidden rounded-bl-[14rem] border-l border-accent/60">
            <picture>
              <source srcSet="/img/secteurs-cee/bureaux.webp" type="image/webp" />
              <img
                src="/img/secteurs-cee/bureaux.jpg"
                alt="Immeuble de bureaux vitré en fin de journée"
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover"
              />
            </picture>
            <div aria-hidden className="pointer-events-none absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-primary/60 to-transparent" />
          </div>
          <div className="em-run-border absolute bottom-12 right-[5%] w-[173px] rounded-[22px] border border-white/25 bg-[#152614]/70 px-6 py-5 shadow-2xl backdrop-blur-xl">
            <p className="text-base text-white/90">Jusqu'à</p>
            <p className="font-display -my-0.5 text-[64px] font-extrabold leading-none tracking-tight text-accent">×5</p>
            <p className="text-base leading-snug text-white/90">la prime CEE standard</p>
          </div>
        </div>

        <div className="em-container relative z-10 w-full py-16 md:py-20">
          <div className="lg:max-w-[44%]">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Professionnels &amp; collectif</p>
            <h2 className="font-display mt-3 text-3xl font-extrabold tracking-tight md:text-4xl lg:text-[44px] lg:leading-[1.12]">
              Valorisez vos kWh cumac au meilleur prix
            </h2>
            <p className="mt-5 max-w-md leading-relaxed text-primary-foreground/80">
              Pour un site tertiaire, une copropriété ou un parc multi-sites, nous nous concentrons sur les opérations finançables sans reste à charge : votre projet est couvert par la prime, du montage du dossier au contrôle après chantier.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-7">
              <Button asChild size="lg" className="h-14 min-w-[280px] rounded-full bg-accent pl-8 pr-6 font-bold text-accent-foreground hover:bg-accent/90">
                <Link to="/contact?type=cee-pro" className="flex items-center justify-between">
                  Faire chiffrer mon site <ArrowRight className="h-5 w-5" />
                </Link>
              </Button>
              <Link
                to="/professionnels/primes-cee"
                className="border-b border-accent pb-1 text-sm font-semibold text-accent transition-colors hover:border-accent/60"
              >
                Découvrir notre approche
              </Link>
            </div>
          </div>

          {/* Photo mobile */}
          <div className="relative mt-10 lg:hidden">
            <div className="h-[300px] overflow-hidden rounded-3xl sm:h-[380px]">
              <picture>
                <source srcSet="/img/secteurs-cee/bureaux.webp" type="image/webp" />
                <img
                  src="/img/secteurs-cee/bureaux.jpg"
                  alt="Immeuble de bureaux vitré en fin de journée"
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover"
                />
              </picture>
            </div>
            <div className="em-run-border absolute bottom-4 right-3 w-[160px] rounded-2xl border border-white/15 bg-primary/70 p-4 shadow-xl backdrop-blur-md">
              <p className="text-sm font-medium text-white/90">Jusqu'à</p>
              <p className="font-display my-0.5 text-4xl font-extrabold leading-none text-accent">×5</p>
              <p className="text-[13px] leading-snug text-white/85">la prime CEE standard</p>
            </div>
          </div>
        </div>
      </section>

      {/* Trois piliers CEE pro + bandeau de garanties */}
      <div className="em-container pb-14 pt-10 md:pb-16 md:pt-12">
        <div className="grid gap-5 md:grid-cols-3">
          {[
            {
              img: "/img/pictogrammes_energy_market_svg/pro_tertiaire.svg",
              title: "Tertiaire & résidentiel collectif",
              text: "Coup de pouce chauffage collectif : PAC collectives, chaudières biomasse, raccordement aux réseaux de chaleur — primes bonifiées de ×3 à ×5 le montant CEE standard selon l'équipement, sur toute la 6ᵉ période.",
            },
            {
              img: "/img/pictogrammes_energy_market_svg/pro_industrie.svg",
              title: "Industrie & multi-sites",
              text: "Fiches standardisées (moteurs, air comprimé, récupération de chaleur…) et opérations spécifiques, intégrées à votre stratégie d'achat d'énergie déjà pilotée avec Energy Market.",
            },
            {
              img: "/img/pictogrammes_energy_market_svg/pro_dossier.svg",
              title: "Dossier traité de bout en bout",
              text: "Vérification de l'éligibilité, antériorité, pièces et attestations, jusqu'au contrôle post-chantier et au versement de la prime.",
            },
          ].map((c, i) => (
            <TraceCard key={c.title} index={i} radius={16} accent={TRACE_COLORS[i]} className="rounded-2xl border border-border bg-card text-card-foreground h-full">
              <CardContent className="flex gap-4 p-6">
                <img src={c.img} alt="" aria-hidden loading="lazy" decoding="async" className="h-14 w-14 shrink-0" />
                <div>
                  <h3 className="font-display text-[17px] font-bold leading-snug text-primary">{c.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">{c.text}</p>
                </div>
              </CardContent>
            </TraceCard>
          ))}
        </div>

        <div className="mt-5 grid rounded-2xl border border-[#2C5B44]/15 bg-gradient-to-r from-[#F4F8ED] via-white to-[#F4F8ED] sm:grid-cols-3">
          {[
            { img: "/img/pictogrammes_energy_market_svg/pro_obliges.svg", title: "Sans reste à charge", text: "Nous ciblons les opérations intégralement financées" },
            { img: "/img/pictogrammes_energy_market_svg/pro_dossier.svg", title: "Dossier sécurisé", text: "De la constitution au contrôle post-chantier" },
            { img: "/img/pictogrammes_energy_market_svg/pro_suivi.svg", title: "Suivi jusqu'au versement", text: "Accompagnement complet et réactif" },
          ].map((b, i) => (
            <div key={b.title} className={`flex items-center gap-3.5 px-6 py-4 ${i ? "border-t border-[#CBD89A] sm:border-l sm:border-t-0" : ""}`}>
              <img src={b.img} alt="" aria-hidden loading="lazy" decoding="async" className="h-10 w-10 shrink-0" />
              <div>
                <h4 className="font-display text-[15px] font-bold text-primary">{b.title}</h4>
                <p className="mt-0.5 text-[13px] text-muted-foreground">{b.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <FinalCTA
        title="Faisons le point sur vos gisements"
        text="Décrivez-nous vos bâtiments et vos projets de travaux : nous identifions les opérations valorisables et le montant mobilisable."
      />
    </div>
  );
}
